import axios from "axios";
import { debug, error } from '@/utils/logger';
import { WebAnalytics, normalizeProfileTypeForAnalytics } from '@/lib/analytics';
import { EVENT_AUTH_ERROR, EVENT_ONBOARDING_COMPLETED } from '@/lib/analyticsEvents';
import User from '@/models/user';
import Post from '@/models/post';
import type { Location } from '@/models/locationModel';
import { prepareUtmsForBackend, getUtmsFromCurrentUrl, getUtmsWithFallback, type UtmParameters } from '@/lib/utm';
import { getSignupPlatform } from '@/lib/platform';
import {
  classifyAuthFailure,
  readReauthRequired,
  parseAuthMethodsResponse,
  type AuthMethodsSummary,
} from '@/lib/accountLinking';
import { DEFAULT_LOCATION_SEARCH_RADIUS, DEFAULT_LOCATION_SEARCH_LIMIT } from '@/constants/mapZoom';
import { isRequestAborted, type FetchRequestOptions } from '@/lib/requestAbort';
import type { PlatformBrowseMode } from '@/constants/platformBrowseMode';
import { PUBLIC_POSTS_SEARCH_ENDPOINT } from '@/api/search';
// const API_URL = 'http://localhost:8080';
const API_URL = process.env.NEXT_PUBLIC_APP_API_BASE_URL;

export const CREATE_USER = `${API_URL}/api/v2/user`; // Legacy endpoint - consider using UPDATE_USER instead
export const CITY_SEARCH = `${API_URL}/api/v2/location/autocomplete/google`;
export const LOCATIONS = `${API_URL}/api/v2/locations`;
export const FEATURED_LOCATIONS = `${API_URL}/api/v2/locations/featured`;
export const CHECK_USER = `${API_URL}/api/v2/user/profile`;
export const FETCH_USER = `${API_URL}/api/v2/user`;
export const USER_AUTHENTICATED = `${API_URL}/api/v1/user/authenticated`;
export const AUTH_METHODS = `${API_URL}/api/v2/auth/methods`;
export const AUTH_LINK = `${API_URL}/api/v2/auth/link`;
export const AUTH_PASSWORD = `${API_URL}/api/v2/auth/password`;
export const UPDATE_USER = `${API_URL}/api/v1/user`;
/** Soft-delete authenticated user (sets is_deleted). Backend: GET only — not DELETE on /user. */
export const DELETE_USER = `${API_URL}/api/v1/user/delete`;
export const FEATURED_POSTS = `${API_URL}/api/v2/posts/post_types`;
export const CREATORS_LIST = `${API_URL}/api/v2/users/featured`;
export const ALL_USERS = `${API_URL}/api/v1/users`;
export const PROFILE_LIST = `${API_URL}/api/v2/posts/user`;
export const GET_SAVED_POST = `${API_URL}/api/v2/saves/posts`;
export const HIDE_POST = `${API_URL}/api/v1/post/hide`;
export const USER_COUNTRIES_COUNT = (userId: string) => `${API_URL}/api/v1/user/countries/count/${userId}`;
export const USER_COUNTRIES = (userId: string) => `${API_URL}/api/v1/user/countries/${userId}`;
export const USER_EXPERIENCES = (userId: string) => `${API_URL}/api/v1/user/experiences/${userId}`;
export const USER_STATS = (userId: string) => `${API_URL}/api/v1/user/stats/${userId}`;

// Public landing page endpoints (no authentication required)
export const PUBLIC_FEATURED_POSTS = `${API_URL}/api/v2/public/featured-posts`;
export const PUBLIC_CREATORS_LIST = `${API_URL}/api/v2/public/featured-creators`;
export const PUBLIC_FEATURED_LOCATIONS = `${API_URL}/api/v2/public/featured-city-locations`;
/** OPEN browse guest featured users — mirrors GET /api/v2/users/featured (OPEN mode only). */
export const PUBLIC_OPEN_BROWSE_USERS_FEATURED = `${API_URL}/api/v2/public/users/featured`;

export interface CityObject {
  identifying_name?: string;
  city_identifying_name?: string;
  name?: string;
  city?: string;
  _id?: string;
  [key: string]: unknown;
}

export type CityPayload = string | CityObject;

/**
 * Safe Advisor Pro summary on public profiles. Deliberately carries no Stripe
 * identifiers — just enough to render the storefront badge.
 */
/**
 * Advisor Pro summary exposed on profile payloads (public and authenticated).
 * Deliberately carries no Stripe data — just enough for the badge and the
 * "Plan Your Trip" popup.
 */
export type PublicAdvisorProSummary = {
  /** Entitlement; already accounts for `past_due` not being entitled. */
  is_pro?: boolean;
  /** Credential verified by an admin. */
  is_verified?: boolean;
  /** Admin surfaces can also receive richer status fields. */
  status?: 'none' | 'pending_payment' | 'active' | 'past_due' | 'canceled';
  verification_status?: 'unsubmitted' | 'pending' | 'verified' | 'rejected';
  subscription_type?: 'Monthly' | 'Annual' | null;
  /**
   * Advisor's configured services, used by the "Plan Your Trip" popup.
   * Typed loosely here to avoid a cycle with `@/api/advisorPro`; consumers
   * narrow it to `AdvisorProService[]`.
   */
  services?: unknown[];
  /**
   * Average time the advisor takes to first reply to a trip request, in
   * minutes. Absent until they have response history.
   */
  avg_response_minutes?: number | null;
};

export type UserProfilePayload = {
  name?: string;
  email?: string;
  bio?: string;
  city?: CityPayload;
  city_identifying_name?: string;
  agency_name?: string;
  iata_number?: string;
  instagram_username?: string | null;
  tiktok_username?: string | null;
  youtube_username?: string | null;
  substack_username?: string | null;
  /**
   * Public profile handle. Owned by the dedicated PATCH /api/v1/user/username
   * endpoint via `updateUsername()`; not written through the generic
   * profile-update pipeline. Present here so downstream types can carry it.
   */
  username?: string | null;
  website_label?: string;
  website_url?: string;
  book_url?: string;
  dob?: string;
  date_of_birth?: string;
  post_frequency?: string;
  profile_type?: string;
  traveller_types?: string[];
  travel_with_types?: string[];
  large_avatar_url?: string;
  small_avatar_url?: string;
  avatar_url?: string;
  profile_image_url?: string;
  hero_image_url?: string;
  hero_image_secondary_url?: string;
  dream_destinations?: string[];
  dream_destination_reason?: string;
  /**
   * v2 only: set to `true` on the final onboarding call. Omit on intermediate
   * step saves so the backend does not mark onboarding as complete.
   * Handled separately from PROFILE_CREATE_ALLOWED_FIELDS in `createUser`.
   */
  did_complete_onboarding?: boolean;
};

/** Profile fields that may appear in PROFILE_CREATE/UPDATE allowed-field lists. */
type ProfileFieldKey = Exclude<keyof UserProfilePayload, 'did_complete_onboarding'>;

const ARRAY_FIELD_KEYS = ['traveller_types', 'travel_with_types', 'dream_destinations'] as const;
type ArrayFieldKey = typeof ARRAY_FIELD_KEYS[number];
const SOCIAL_HANDLE_FIELD_KEYS = [
  'instagram_username',
  'tiktok_username',
  'youtube_username',
  'substack_username',
] as const;
type SocialHandleFieldKey = typeof SOCIAL_HANDLE_FIELD_KEYS[number];

const isArrayField = (field: ProfileFieldKey): field is ArrayFieldKey =>
  ARRAY_FIELD_KEYS.includes(field as ArrayFieldKey);
const isSocialHandleField = (field: ProfileFieldKey): field is SocialHandleFieldKey =>
  SOCIAL_HANDLE_FIELD_KEYS.includes(field as SocialHandleFieldKey);

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

const getAuthHeaders = () => {
  const rawAccessToken = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;

  if (!rawAccessToken) {
    error("[auth:getAuthHeaders] No access token found in localStorage");
    throw new Error('Authentication token not found. User must be logged in.');
  }

  const accessToken = rawAccessToken.startsWith('Bearer ')
    ? rawAccessToken.substring(7)
    : rawAccessToken;

  const headers: Record<string, string> = {
    'Authorization': accessToken
  };

  const rawIdToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;
  if (rawIdToken) {
    const idToken = rawIdToken.startsWith('Bearer ') ? rawIdToken.substring(7) : rawIdToken;
    headers['x-id-token'] = idToken;
  }

  return headers;
};

export const PROFILE_CREATE_ALLOWED_FIELDS: ProfileFieldKey[] = [
  'name', 'email', 'bio', 'city', 'city_identifying_name', 'agency_name', 'iata_number',
  'instagram_username', 'tiktok_username', 'youtube_username', 'substack_username',
  'website_label', 'website_url', 'book_url', 'dob', 'date_of_birth',
  'post_frequency', 'profile_type', 'traveller_types', 'travel_with_types',
  'large_avatar_url', 'small_avatar_url', 'avatar_url', 'profile_image_url',
  'hero_image_url', 'hero_image_secondary_url', 'dream_destinations', 'dream_destination_reason',
];

// Email is intentionally excluded here — the backend treats email as immutable after creation.
export const PROFILE_UPDATE_ALLOWED_FIELDS: ProfileFieldKey[] = PROFILE_CREATE_ALLOWED_FIELDS.filter(
  (field) => field !== 'email'
);

/**
 * Structured error thrown when the backend rejects an onboarding completion
 * attempt because required profile fields are missing.
 */
export class OnboardingIncompleteError extends Error {
  readonly code = 'ONBOARDING_INCOMPLETE' as const;
  readonly missingFields: string[];

  constructor(missingFields: string[]) {
    super('Onboarding profile is incomplete');
    this.name = 'OnboardingIncompleteError';
    this.missingFields = missingFields;
  }
}

export function isOnboardingIncompleteError(err: unknown): err is OnboardingIncompleteError {
  if (!err || typeof err !== 'object') return false;
  return (err as OnboardingIncompleteError).code === 'ONBOARDING_INCOMPLETE';
}

/**
 * POST /api/v2/user — patch-only upsert.
 *
 * v2 semantics: only keys present in the body are updated; omitted fields are
 * left unchanged in the DB. `did_complete_onboarding` is set to `true` only
 * when explicitly included in the payload.
 *
 * On completion attempts the backend validates that the profile is complete and
 * returns 400 `ONBOARDING_INCOMPLETE` with `missing_fields` if not.
 */
export async function createUser(data: UserProfilePayload): Promise<User> {
  try {
    const rawAccessToken = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
    const rawIdToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;

    if (!rawAccessToken) {
      error("[createUser] No access token found in localStorage");
      throw new Error('Authentication token not found. User must be logged in before creating profile.');
    }

    const accessToken = rawAccessToken.startsWith('Bearer ')
      ? rawAccessToken.substring(7)
      : rawAccessToken;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': accessToken,
    };

    const idToken = rawIdToken
      ? (rawIdToken.startsWith('Bearer ') ? rawIdToken.substring(7) : rawIdToken)
      : null;

    if (idToken) {
      headers['x-id-token'] = idToken;
    }

    const cleanedData: Record<string, unknown> = {};
    const allowedFields = PROFILE_CREATE_ALLOWED_FIELDS;

    allowedFields.forEach((field) => {
      const value = data?.[field];

      if (value === undefined) {
        return;
      }

      // v2 supports null to clear a field
      if (value === null) {
        cleanedData[field] = null;
        return;
      }

      if (isArrayField(field)) {
        if (isStringArray(value)) {
          cleanedData[field] = value;
        }
        return;
      }

      if (typeof value === 'object' && !Array.isArray(value)) {
        if (field === 'city') {
          cleanedData[field] = value;
        }
        return;
      }

      if (typeof value === 'string' && value !== '') {
        cleanedData[field] = value;
      }
    });

    // Forward did_complete_onboarding when explicitly set
    if (data.did_complete_onboarding === true) {
      cleanedData.did_complete_onboarding = true;
    }

    const utmData = getUtmsFromCurrentUrl();
    if (utmData) {
      const utmPayload = prepareUtmsForBackend(utmData);
      if (utmPayload) {
        Object.assign(cleanedData, utmPayload);
      }
    }

    const signupPlatform = getSignupPlatform();
    if (signupPlatform !== 'unknown') {
      cleanedData.platform = signupPlatform;
    }

    const response = await axios.post(`${CREATE_USER}`, cleanedData, { headers });

    if (data.did_complete_onboarding && response.data?._id) {
      WebAnalytics.track(EVENT_ONBOARDING_COMPLETED, {
        user_id: response.data._id,
        profile_type: normalizeProfileTypeForAnalytics(response.data.profile_type),
      });
    }

    return new User(response.data);
  } catch (err) {
    // Surface ONBOARDING_INCOMPLETE as a typed error
    if (axios.isAxiosError(err)) {
      const classified = classifyAuthFailure(err.response?.status, err.response?.data);
      if (classified) throw classified;
    }

    if (
      axios.isAxiosError(err) &&
      err.response?.status === 400 &&
      err.response.data?.code === 'ONBOARDING_INCOMPLETE'
    ) {
      const missingFields: string[] = err.response.data.missing_fields ?? [];
      throw new OnboardingIncompleteError(missingFields);
    }

    error("Error creating user:", err);

    if (axios.isAxiosError(err)) {
      const { config, response } = err;

      const errorMessage = response?.data?.message || response?.data?.error || err.message || 'Unknown error';
      WebAnalytics.trackError(EVENT_AUTH_ERROR, {
        error_type: 'user_creation_failed',
        errorMessage: errorMessage,
        status_code: response?.status,
        endpoint: config?.url,
      });

      if (config) {
        error("Attempted URL:", config.url);
        error("Request method:", config.method);
        error("Request headers:", Object.keys(config.headers || {}));
      }

      if (response) {
        error("Response status:", response.status);
        error("Response data:", response.data);
        error("Response headers:", response.headers);

        if (response.status === 403) {
          error("Authentication failed - Authorization header may be missing or invalid");
        }

        const message = (response.data as { message?: string } | undefined)?.message;
        if (message) {
          error("Error message:", message);
        }

        const responseError = (response.data as { error?: string } | undefined)?.error;
        if (responseError) {
          error("Error details:", responseError);
        }
      }

      if (config?.data) {
        try {
          const sentData = typeof config.data === 'string'
            ? JSON.parse(config.data)
            : config.data;
          error("Request data sent:", JSON.stringify(sentData, null, 2));
        } catch {
          error("Request data (raw):", config.data);
        }
      }
    }

    throw err;
  }
}

export async function searchCity(query: string) {
  try {
    if (!query) return [];
    const response = await axios.get(CITY_SEARCH, {
      params: {
        input: query,
        filterMode: "cities",  // Replaces cityOnly: true, allTypes: false
      },
    });
    
    debug('[searchCity] Request parameters:', {
      input: query,
      filterMode: 'cities',
      lat: 'unset',
      long: 'unset',
    });
    
    return response.data;
  } catch (err) {
    error("Error searching city:", err);
    return [];
  }
}

export async function fetchLocations(
  lat: number,
  lng: number,
  radius = 10,
  limit = 20,
  minPosts = 2
) {
  try {
    const response = await axios.get(LOCATIONS, {
      params: { lat, lng, radius, limit, minPosts, photoFormat: "identifiers" },
    });
    return response.data;
  } catch (err) {
    error("Error fetching locations:", err);
    return [];
  }
}

export async function fetchFeaturedLocations(limit = 10, minPosts = 2) {
  try {
    const response = await axios.get(FEATURED_LOCATIONS, {
      params: { limit, minPosts, photoFormat: "identifiers" },
    });
    return response.data;
  } catch (err) {
    error("Error fetching featured locations:", err);
    return [];
  }
}

/**
 * Custom error type for banned users.
 * Thrown when a banned user attempts to authenticate.
 */
export interface BannedUserError extends Error {
  isBanned: true;
  bannedMessage: string;
}

/**
 * Type guard to check if an error is a banned user error.
 */
export function isBannedUserError(error: unknown): error is BannedUserError {
  return (
    error !== null &&
    typeof error === 'object' &&
    'isBanned' in error &&
    (error as BannedUserError).isBanned === true
  );
}

/**
 * Syncs user with backend using the authenticated endpoint (same as mobile app).
 * This endpoint handles both new and returning users, and returns the user with
 * did_complete_onboarding flag from the database.
 *
 * @param email - User's email address
 * @param cognitoUsername - Cognito username (from token 'sub' or 'cognito:username')
 * @param utmData - Optional UTM parameters for tracking user acquisition
 * @returns User object with did_complete_onboarding flag
 * @throws BannedUserError if the user account is banned
 */
export async function syncUserWithBackend(email: string, cognitoUsername: string, utmData?: UtmParameters | null): Promise<User> {
  debug('🔐 [syncUserWithBackend] Function called', {
    email: email || 'EMPTY',
    cognitoUsername: cognitoUsername || 'EMPTY',
    hasUtmData: !!utmData
  });

  try {
    const payload: {
      email: string;
      cognitoUsername: string;
      [key: string]: unknown;
    } = {
      email,
      cognitoUsername,
    };
    const signupPlatform = getSignupPlatform();
    if (signupPlatform !== 'unknown') {
      payload.platform = signupPlatform;
    }

    // Add UTM parameters if available (cookie fallback for first authenticated sync)
    const resolvedUtms = utmData ?? getUtmsWithFallback();
    if (resolvedUtms) {
      const utmPayload = prepareUtmsForBackend(resolvedUtms);
      if (utmPayload) {
        Object.assign(payload, utmPayload);
      }
    }

    debug('🔐 [syncUserWithBackend] Making API request', {
      endpoint: USER_AUTHENTICATED,
      payloadEmail: payload.email || 'EMPTY',
      payloadCognitoUsername: payload.cognitoUsername || 'EMPTY'
    });

    const response = await axios.put(`${USER_AUTHENTICATED}`, payload, {
      headers: getAuthHeaders(),
    });
    const userData = response.data;

    debug('🔐 [syncUserWithBackend] Backend response received', {
      hasUserData: !!userData,
      userId: userData?._id || 'none',
      emailFromResponse: userData?.email || 'not in response',
      didCompleteOnboarding: userData?.did_complete_onboarding ?? 'not in response',
      emailFromRequest: email || 'EMPTY'
    });

    debug('🔐 [syncUserWithBackend] Returning User object', {
      userId: userData?._id || 'none',
      email: userData?.email || 'none',
      didCompleteOnboarding: userData?.did_complete_onboarding ?? 'not set'
    });

    return new User(userData);
  } catch (err) {
    // Check for banned or deleted user errors (403 with "Account banned" or "Account deleted")
    if (axios.isAxiosError(err)) {
      const classified = classifyAuthFailure(err.response?.status, err.response?.data);
      if (classified) throw classified;

      const status = err.response?.status;
      const errorBody = err.response?.data;
      
      if (status === 403 && errorBody?.error === 'Account banned') {
        error('🔐 [syncUserWithBackend] User account is banned', {
          email: email || 'EMPTY',
          message: errorBody.message
        });
        
        // Create a custom error with banned flag
        const bannedError = new Error(errorBody.message || 'Your account has been suspended. Please contact support for assistance.') as Error & { isBanned: boolean; bannedMessage: string };
        bannedError.isBanned = true;
        bannedError.bannedMessage = errorBody.message || 'Your account has been suspended. Please contact support for assistance.';
        throw bannedError;
      }
      
      if (status === 403 && errorBody?.error === 'Account deleted') {
        error('🔐 [syncUserWithBackend] User account is deleted', {
          email: email || 'EMPTY',
          message: errorBody.message
        });
        
        // Create a custom error with deleted flag
        const deletedError = new Error(errorBody.message || 'This account has been deleted. Please contact support for assistance.') as Error & { isDeleted: boolean; deletedMessage: string };
        deletedError.isDeleted = true;
        deletedError.deletedMessage = errorBody.message || 'This account has been deleted. Please contact support for assistance.';
        throw deletedError;
      }
    }
    
    error('🔐 [syncUserWithBackend] Error occurred', {
      error: err,
      email: email || 'EMPTY',
      cognitoUsername: cognitoUsername || 'EMPTY'
    });
    error("Error syncing user with backend:", err);
    throw err;
  }
}

export async function getAuthMethods(): Promise<AuthMethodsSummary> {
  const response = await axios.get(AUTH_METHODS, { headers: getAuthHeaders() });
  return parseAuthMethodsResponse(response.data);
}

export async function linkAuthProvider(pendingIdToken: string): Promise<{ reauthRequired: boolean }> {
  try {
    const response = await axios.post(
      AUTH_LINK,
      { id_token: pendingIdToken },
      { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } },
    );
    return { reauthRequired: readReauthRequired(response.data) };
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const classified = classifyAuthFailure(err.response?.status, err.response?.data);
      if (classified) throw classified;
    }
    error('[linkAuthProvider] Failed to link sign-in method', err);
    throw err;
  }
}

export async function setAuthPassword(password: string): Promise<{ reauthRequired: boolean }> {
  try {
    const response = await axios.post(
      AUTH_PASSWORD,
      { password },
      { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } },
    );
    return { reauthRequired: readReauthRequired(response.data) };
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const classified = classifyAuthFailure(err.response?.status, err.response?.data);
      if (classified) throw classified;
    }
    error('[setAuthPassword] Failed to add password', err);
    throw err;
  }
}

export async function unlinkAuthProvider(provider: 'google' | 'apple'): Promise<void> {
  try {
    await axios.delete(`${AUTH_METHODS}/${provider}`, { headers: getAuthHeaders() });
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const classified = classifyAuthFailure(err.response?.status, err.response?.data);
      if (classified) throw classified;
    }
    error('[unlinkAuthProvider] Failed to remove sign-in method', err);
    throw err;
  }
}

export async function fetchProfile(
  userId: string,
  options?: FetchRequestOptions
): Promise<User> {
  try {
    const headers = getAuthHeaders();
    const url = `${FETCH_USER}?user_id=${userId}`;
    const response = await axios.get(url, {
      headers,
      signal: options?.signal,
    });
    return new User(response.data);
  } catch (err) {
    if (!isRequestAborted(err)) {
      error("Error fetching user profile:", err);
    }
    throw err;
  }
}

/**
 * Look up a user by their public handle. Returns the same shape as
 * `fetchProfile` so `/profile/:idOrUsername` can dispatch on URL segment
 * shape (see `isObjectId` in `src/utils/username.ts`) and reuse the rest of
 * the profile pipeline unchanged.
 */
export async function fetchProfileByHandle(
  username: string,
  options?: FetchRequestOptions
): Promise<User> {
  try {
    const headers = getAuthHeaders();
    const url = `${FETCH_USER}?username=${encodeURIComponent(username)}`;
    const response = await axios.get(url, {
      headers,
      signal: options?.signal,
    });
    return new User(response.data);
  } catch (err) {
    if (!isRequestAborted(err)) {
      error('Error fetching user profile by handle:', err);
    }
    throw err;
  }
}

export type UsernameAvailability =
  | { available: true; username: string }
  | { available: false; reason: string; message?: string };

/**
 * Preflight a candidate handle against the backend. Format failures short-
 * circuit server-side too; a `taken` result means the handle passed
 * validation but is already claimed. Called with debounce from the signup +
 * editor UIs. Both callers run authenticated (Cognito session is in place
 * by the time the onboarding username step renders).
 */
export async function checkUsernameAvailable(
  username: string,
  options?: FetchRequestOptions
): Promise<UsernameAvailability> {
  try {
    const url = `${API_URL}/api/v1/user/username-available?username=${encodeURIComponent(username)}`;
    const response = await axios.get(url, { headers: getAuthHeaders(), signal: options?.signal });
    return response.data as UsernameAvailability;
  } catch (err) {
    if (!isRequestAborted(err)) {
      error('Error checking username availability:', err);
    }
    return { available: false, reason: 'network', message: 'Could not reach the server.' };
  }
}

export type UpdateUsernameResult =
  | { ok: true; user: User }
  | { ok: false; code: string; message: string };

/**
 * Persist a username for the signed-in user. Pass `null` to clear the
 * handle (removes it from the record; profile becomes reachable only via
 * `/profile/<_id>` again).
 */
export async function updateUsername(
  username: string | null,
  options?: FetchRequestOptions
): Promise<UpdateUsernameResult> {
  try {
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    const url = `${API_URL}/api/v1/user/username`;
    const response = await axios.patch(
      url,
      { username },
      { headers, signal: options?.signal }
    );
    return { ok: true, user: new User(response.data) };
  } catch (err) {
    if (isRequestAborted(err)) throw err;
    // Axios error with typed response body from the API.
    const axiosErr = err as { response?: { status?: number; data?: { code?: string; message?: string } } };
    const status = axiosErr.response?.status;
    const body = axiosErr.response?.data;
    if (status === 409) {
      return { ok: false, code: body?.code || 'taken', message: body?.message || 'That username is taken.' };
    }
    if (status === 400 && body?.code) {
      return { ok: false, code: body.code, message: body.message || 'That username is not allowed.' };
    }
    error('Error updating username:', err);
    return { ok: false, code: 'unknown', message: 'Could not save username.' };
  }
}

export async function fetchFeaturedPosts(
  limit = 10, 
  tags: string[] = [],
  cityIdentifyingName?: string,
  options?: FetchRequestOptions
): Promise<Post[]> {
  try {
    // When tags is empty (no pills selected), don't send tags filter to show ALL posts
    // When tags are selected, send only the selected tags to filter
    // NOTE: Sending all tags when none selected was too restrictive - posts need at least ONE matching tag
    const tagsToSend = tags.length > 0 ? tags : [];
    
    // Get auth headers explicitly (backend requires Authorization header for /api/v2/* routes)
    const headers: Record<string, string> = {};
    const rawAccessToken = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
    
    if (rawAccessToken) {
      // Backend expects raw JWT token without "Bearer " prefix
      const accessToken = rawAccessToken.startsWith('Bearer ')
        ? rawAccessToken.substring(7)
        : rawAccessToken;
      headers['Authorization'] = accessToken;
      
      const rawIdToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;
      if (rawIdToken) {
        const idToken = rawIdToken.startsWith('Bearer ') ? rawIdToken.substring(7) : rawIdToken;
        headers['x-id-token'] = idToken;
      }
    }
    
    const requestParams: Record<string, string | number> = { 
      limit, 
      postTypes: "Itinerary,List,Place", 
      photoFormat: "identifiers",
    };
    
    // Add optional tags filter
    if (tagsToSend.length > 0) {
      requestParams.tags = tagsToSend.join(",");
    }
    
    // Add optional city filter
    if (cityIdentifyingName) {
      requestParams.cityIdentifyingName = cityIdentifyingName;
    }
    
    const response = await axios.get(FEATURED_POSTS, {
      params: requestParams,
      headers: Object.keys(headers).length > 0 ? headers : undefined,
      signal: options?.signal,
    });
    
    // Backend returns an array directly, but handle edge cases defensively
    let postsData = response.data;
    
    // Defensive check: if response is a string (shouldn't happen, but handle gracefully)
    if (typeof postsData === 'string') {
      if (postsData.trim() === '') {
        return [];
      }
      try {
        postsData = JSON.parse(postsData);
      } catch (parseError) {
        error('[fetchFeaturedPosts] Failed to parse string response as JSON:', parseError);
        return [];
      }
    }
    
    // Ensure we have an array before mapping
    if (!Array.isArray(postsData)) {
      error('[fetchFeaturedPosts] Response is not an array:', {
        type: typeof postsData,
        value: postsData
      });
      return [];
    }
    
    const posts = postsData.map((item: Record<string, unknown>) => {
      try {
        return new Post(item);
      } catch (postError) {
        error('[fetchFeaturedPosts] Error creating Post object:', {
          error: postError,
          itemId: item?._id
        });
        return null;
      }
    }).filter((post): post is Post => post !== null);
    
    return posts;
  } catch (err) {
    if (isRequestAborted(err)) {
      return [];
    }
    error("Error fetching featured posts:", err);
    if (axios.isAxiosError(err)) {
      error('[fetchFeaturedPosts] Error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message,
        url: err.config?.url
      });
      
      // If 403, log authentication issue
      if (err.response?.status === 403) {
        error('[fetchFeaturedPosts] Authentication failed - 403 Forbidden. Check if Authorization header is being sent.');
      }
    }
    return [];
  }
}

/**
 * Fetches public featured posts for unauthenticated users
 * Uses public endpoint that doesn't require authentication
 * Backend returns: {posts: Post[], limit: number, message: string}
 */
export async function fetchPublicFeaturedPosts(
  limit = 5, 
  _tags: string[] = [],
  cityIdentifyingName?: string,
  options?: FetchRequestOptions
): Promise<Post[]> {
  const maxRetries = 5;
  let retryCount = 0;
  let delay = 1500; // Start with 1.5s delay

  while (retryCount <= maxRetries) {
    try {
      const params: Record<string, string | number> = { 
        limit,
        postTypes: "Itinerary,List,Place",
        photoFormat: "identifiers"
      };
      
      // Add optional city filter
      if (cityIdentifyingName) {
        params.cityIdentifyingName = cityIdentifyingName;
      }
      
      if (options?.signal?.aborted) {
        return [];
      }
      const response = await axios.get(PUBLIC_FEATURED_POSTS, {
        params,
        signal: options?.signal,
      });
      
      // Backend returns {posts: [...], limit: number, message: "..."}
      const postsArray = response.data?.posts || response.data;
      
      if (Array.isArray(postsArray)) {
        return postsArray.map((item: Record<string, unknown>) => new Post(item));
      }
      
      // If response.data is already an array (fallback case)
      if (Array.isArray(response.data)) {
        return response.data.map((item: Record<string, unknown>) => new Post(item));
      }
      
      return [];
    } catch (err: unknown) {
      if (isRequestAborted(err)) {
        return [];
      }
      const errWithResponse = err as { response?: { status?: number } } | null;
      // Handle rate limiting (429) - retry with exponential backoff
      if (errWithResponse?.response?.status === 429 && retryCount < maxRetries) {
        retryCount++;
        debug(`[fetchPublicFeaturedPosts] Rate limited (429), retry ${retryCount}/${maxRetries} after ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        delay *= 2; // Exponential backoff: 1.5s, 3s, 6s, 12s, 24s
        continue;
      }
      
      // For other errors or max retries exceeded, log and return empty array
      if (errWithResponse?.response?.status === 429) {
        debug(`[fetchPublicFeaturedPosts] Rate limited - max retries (${maxRetries}) exceeded, returning empty array`);
      } else {
        error(`[fetchPublicFeaturedPosts] Error fetching public featured posts (attempt ${retryCount + 1}):`, err);
      }
      return [];
    }
  }
  
  return [];
}

/**
 * Fetches featured posts for guest OPEN browse.
 * Uses GET /api/v2/public/posts/post_types (OPEN mode only; mirrors authenticated post_types).
 */
export async function fetchOpenBrowseFeaturedPosts(
  limit = 10,
  tags: string[] = [],
  cityIdentifyingName?: string,
  options?: FetchRequestOptions
): Promise<Post[]> {
  try {
    if (options?.signal?.aborted) {
      return [];
    }

    const params: Record<string, string | number> = {
      limit,
      postTypes: 'Itinerary,List,Place',
      photoFormat: 'identifiers',
    };

    if (tags.length > 0) {
      params.tags = tags.join(',');
    }
    if (cityIdentifyingName) {
      params.cityIdentifyingName = cityIdentifyingName;
    }

    const response = await axios.get(PUBLIC_POSTS_SEARCH_ENDPOINT, {
      params,
      signal: options?.signal,
    });

    const postsData = response.data;
    const postsArray = Array.isArray(postsData)
      ? postsData
      : postsData?.posts ?? [];

    if (!Array.isArray(postsArray)) {
      return [];
    }

    return postsArray.map((item: Record<string, unknown>) => new Post(item));
  } catch (err: unknown) {
    if (isRequestAborted(err)) {
      return [];
    }
    error('[fetchOpenBrowseFeaturedPosts] Error fetching open-browse featured posts:', err);
    return [];
  }
}

/**
 * Fetches all itineraries with pagination support for admin dashboard
 * Uses GET /api/v2/posts/post_types?postTypes=Itinerary&limit=limit
 * @param limit - Number of itineraries to fetch (optional, API default is 25)
 * @returns Array of itinerary posts
 */
export async function fetchAllItineraries(limit?: number): Promise<Post[]> {
  try {
    const params: { postTypes: string; limit?: number; photoFormat: string } = { postTypes: "Itinerary", photoFormat: "identifiers" };
    if (limit !== undefined) {
      params.limit = limit;
    }
    const response = await axios.get(FEATURED_POSTS, { params });
    return Array.isArray(response.data)
      ? response.data.map((item: Record<string, unknown>) => new Post(item))
      : [];
  } catch (err) {
    error("Error fetching all itineraries:", err);
    return [];
  }
}

export async function fetchSavedPostsforUser(): Promise<Post[]> {
  try {
    const response = await axios.get(GET_SAVED_POST, {
      params: { photoFormat: "identifiers" }
    });
    return Array.isArray(response.data)
      ? response.data.map((item: Record<string, unknown>) => new Post(item))
      : [];
  } catch (err) {
    error("Error fetching featured locations:", err);
    return [];
  }
}

/**
 * @deprecated Use fetchAllUserPosts instead and filter client-side by post_type
 * This function is kept for backward compatibility but will be removed in a future version
 */
export async function fetchPosts(
  userId: string,
  limit = 10,
  tags: string[] = []
): Promise<Post[]> {
  try {
    const response = await axios.get(PROFILE_LIST, {
      params: { user_id: userId, limit, onlyPosts: true, tags: tags.join(","), photoFormat: "identifiers" },
    });
    return Array.isArray(response.data)
      ? response.data.map((item: Record<string, unknown>) => new Post(item))
      : [];
  } catch (err) {
    error("Error fetching featured locations:", err);
    return [];
  }
}

/**
 * @deprecated Use fetchAllUserPosts instead and filter client-side by post_type === PostType.Itinerary
 * This function is kept for backward compatibility but will be removed in a future version
 */
export async function fetchItineraries(
  userId: string,
  limit = 10,
  tags: string[] = []
): Promise<Post[]> {
  try {
    const response = await axios.get(PROFILE_LIST, {
      params: {
        user_id: userId,
        limit,
        onlyItineraries: true, // Fixed: correct spelling (was onlyItineraires)
        tags: tags.join(","),
        photoFormat: "identifiers",
      },
    });
    return Array.isArray(response.data)
      ? response.data.map((item: Record<string, unknown>) => new Post(item))
      : [];
  } catch (err) {
    error("Error fetching featured locations:", err);
    return [];
  }
}

export async function fetchDrafts(
  userId: string,
  limit = 10,
  tags: string[] = []
): Promise<Post[]> {
  try {
    const response = await axios.get(PROFILE_LIST, {
      params: {
        user_id: userId,
        limit,
        onlyDrafts: true,
        tags: tags.join(","),
        photoFormat: "identifiers",
      },
    });
    return Array.isArray(response.data)
      ? response.data.map((item: Record<string, unknown>) => new Post(item))
      : [];
  } catch (err) {
    error("Error fetching featured locations:", err);
    return [];
  }
}

/**
 * Pagination response interface for user posts
 */
export interface PaginatedPostsResponse {
  posts: Post[];
  pagination: {
    page: number;
    pageSize: number;
    totalPosts: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

/**
 * Fetches posts for a user with pagination support
 * This replaces separate fetchItineraries/fetchPosts calls with a single unified call
 * 
 * @param userId - User ID
 * @param options - Pagination and filter options
 * @param options.page - Page number (1-indexed), enables pagination mode. If not provided, uses legacy mode.
 * @param options.limit - Posts per page (default: 20, max: 100). Only used when page is provided.
 * @param options.allowPrivate - Include private posts (true if viewing own profile)
 * @param options.allowDrafts - Include drafts (true if viewing own profile)
 * @returns Paginated response with posts and pagination metadata, or array in legacy mode
 */
export async function fetchAllUserPosts(
  userId: string,
  options: {
    page?: number;
    limit?: number;
    allowPrivate?: boolean;
    allowDrafts?: boolean;
    searchString?: string;
  } = {}
): Promise<PaginatedPostsResponse> {
  try {
    debug('[fetchAllUserPosts] Fetching user posts', {
      userId,
      page: options.page,
      limit: options.limit,
      allowPrivate: options.allowPrivate,
      allowDrafts: options.allowDrafts
    });

    const params: Record<string, string | number> = {
      user_id: userId,
      photoFormat: "identifiers",
    };

    // Add page parameter to enable pagination mode
    if (options.page !== undefined) {
      params.page = options.page;
      params.limit = options.limit || 20;
    } else {
      // Legacy mode: use limit if provided (for backwards compatibility)
      if (options.limit) {
        params.limit = options.limit;
      }
    }

    if (options.allowPrivate) {
      params.allowPrivate = 'true';
    }

    if (options.allowDrafts) {
      params.allowDrafts = 'true';
    }

    if (options.searchString && options.searchString.trim()) {
      params.searchString = options.searchString.trim();
    }

    const response = await axios.get(PROFILE_LIST, { params });

    // Handle both legacy (array) and paginated (object) responses
    if (Array.isArray(response.data)) {
      // Legacy mode - convert to paginated format for consistency
      const posts = response.data.map((item: Record<string, unknown>) => new Post(item));
      debug('[fetchAllUserPosts] Successfully fetched posts (legacy mode)', {
        userId,
        totalPosts: posts.length,
        postTypes: [...new Set(posts.map(p => p.post_type))]
      });

      return {
        posts,
        pagination: {
          page: 1,
          pageSize: posts.length,
          totalPosts: posts.length,
          totalPages: 1,
          hasNext: false,
          hasPrevious: false,
        },
      };
    }

    // Paginated mode
    const posts = Array.isArray(response.data.posts)
      ? response.data.posts.map((item: Record<string, unknown>) => new Post(item))
      : [];

      debug('[fetchAllUserPosts] Successfully fetched posts (paginated mode)', {
        userId,
        page: response.data.pagination?.page,
        totalPosts: response.data.pagination?.totalPosts,
        postsInResponse: posts.length,
        hasNext: response.data.pagination?.hasNext,
        postTypes: [...new Set(posts.map((p: Post) => p.post_type))]
      });

    return {
      posts,
      pagination: response.data.pagination || {
        page: 1,
        pageSize: posts.length,
        totalPosts: posts.length,
        totalPages: 1,
        hasNext: false,
        hasPrevious: false,
      },
    };
  } catch (err) {
    error("Error fetching user posts:", err);
    // Return empty paginated response on error
    return {
      posts: [],
      pagination: {
        page: options.page || 1,
        pageSize: options.limit || 20,
        totalPosts: 0,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false,
      },
    };
  }
}

export async function searchLocationsData(
  lat: number,
  lng: number,
  radius = DEFAULT_LOCATION_SEARCH_RADIUS,
  limit = DEFAULT_LOCATION_SEARCH_LIMIT,
  minPosts = 2,
  postId = "",
  includeSites = false,
  includeAllTypes = false
) {
  try {
    const params: Record<string, string | number> = { lat, lng, radius, limit, minPosts, photoFormat: "identifiers" };
    if (postId) {
      params.postId = postId;
    }
    if (includeAllTypes) {
      params.includeAllTypes = 'true';
    } else if (includeSites) {
      params.includeSites = 'true';
    }
    const response = await axios.get(LOCATIONS, { params });
    return response.data;
  } catch (err) {
    error("Error searching locations:", err);
    return { locations: [], center: null };
  }
}

/**
 * Fetches public featured city locations for unauthenticated users
 * Uses public endpoint that doesn't require authentication
 * Backend returns: {locations: Location[], center: {lat, lng}, city: string, metadata: {...}, message: string}
 * Returns format compatible with searchLocationsData: {locations: Location[], center: {lat, lng}}
 */
export async function fetchPublicFeaturedCityLocations(includeSites = false, includeAllTypes = false): Promise<{locations: Location[], center: {lat: number, lng: number}}> {
  const maxRetries = 5;
  let retryCount = 0;
  let delay = 1500; // Start with 1.5s delay

  while (retryCount <= maxRetries) {
    try {
      const params: Record<string, string> = { photoFormat: "identifiers" };
      if (includeAllTypes) {
        params.includeAllTypes = 'true';
      } else if (includeSites) {
        params.includeSites = 'true';
      }
      const response = await axios.get(PUBLIC_FEATURED_LOCATIONS, { params });
      
      // Backend returns {locations: [...], center: {...}, city: string, metadata: {...}}
      if (response.data && response.data.locations && response.data.center) {
        return {
          locations: response.data.locations || [],
          center: response.data.center
        };
      }
      
      // If response format is unexpected, return empty
      return {
        locations: [],
        center: { lat: 0, lng: 0 }
      };
    } catch (err: unknown) {
      const errWithResponse = err as { response?: { status?: number } } | null;
      // Handle rate limiting (429) - retry with exponential backoff
      if (errWithResponse?.response?.status === 429 && retryCount < maxRetries) {
        retryCount++;
        debug(`[fetchPublicFeaturedCityLocations] Rate limited (429), retry ${retryCount}/${maxRetries} after ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        delay *= 2; // Exponential backoff: 1.5s, 3s, 6s, 12s, 24s
        continue;
      }
      
      // For other errors, log and return empty/error state
      if (errWithResponse?.response?.status === 429) {
        debug(`[fetchPublicFeaturedCityLocations] Rate limited - max retries (${maxRetries}) exceeded`);
      } else {
        error(`[fetchPublicFeaturedCityLocations] Error fetching public featured city locations (attempt ${retryCount + 1}):`, err);
      }
      
      // Return empty state on error - let the UI handle the error state
      return {
        locations: [],
        center: { lat: 0, lng: 0 }
      };
    }
  }
  
  return {
    locations: [],
    center: { lat: 0, lng: 0 }
  };
}

export async function getCreatorsList(limit = 10): Promise<User[]> {
  try {
    const response = await axios.get(CREATORS_LIST, {
      params: { limit },
    });
    return Array.isArray(response.data)
      ? response.data.map((item: Record<string, unknown>) => new User(item))
      : [];
  } catch (err) {
    error("Error fetching creators list:", err);
    return [];
  }
}

export type FeaturedProfileType = 'creator' | 'advisor';

/**
 * Fetches featured users filtered by profile type.
 * Uses GET /api/v2/users/featured with profile_type (no searchString).
 * Returns plain array of users with traveller_types, recommendation_count, is_following, followers_count.
 */
export async function getFeaturedUsersByProfileType(
  profileType: FeaturedProfileType,
  limit: number = 10,
  options?: FetchRequestOptions
): Promise<User[]> {
  try {
    const cappedLimit = Math.min(Math.max(limit, 1), 50);
    const response = await axios.get(CREATORS_LIST, {
      params: { profile_type: profileType, limit: cappedLimit },
      signal: options?.signal,
    });
    return Array.isArray(response.data)
      ? response.data.map((item: Record<string, unknown>) => new User(item))
      : [];
  } catch (err) {
    if (isRequestAborted(err)) {
      return [];
    }
    error(`Error fetching featured ${profileType}s:`, err);
    return [];
  }
}

/**
 * Fetches public featured creators for unauthenticated users
 * Uses public endpoint that doesn't require authentication
 * Backend returns: {creators: User[], limit: number, message: string}
 */
export async function getPublicCreatorsList(limit = 3): Promise<User[]> {
  const maxRetries = 5;
  let retryCount = 0;
  let delay = 1500; // Start with 1.5s delay

  const cappedLimit = Math.min(Math.max(limit, 1), 50);
  while (retryCount <= maxRetries) {
    try {
      const response = await axios.get(PUBLIC_CREATORS_LIST, {
        params: { limit: cappedLimit },
      });
      
      // Backend returns {creators: [...], limit: number, message: "..."}
      const creatorsArray = response.data?.creators || response.data;
      
      if (Array.isArray(creatorsArray)) {
        return creatorsArray.map((item: Record<string, unknown>) => new User(item));
      }
      
      // If response.data is already an array (fallback case)
      if (Array.isArray(response.data)) {
        return response.data.map((item: Record<string, unknown>) => new User(item));
      }
      
      return [];
    } catch (err: unknown) {
      const errWithResponse = err as { response?: { status?: number } } | null;
      // Handle rate limiting (429) - retry with exponential backoff
      if (errWithResponse?.response?.status === 429 && retryCount < maxRetries) {
        retryCount++;
        debug(`[getPublicCreatorsList] Rate limited (429), retry ${retryCount}/${maxRetries} after ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        delay *= 2; // Exponential backoff: 1.5s, 3s, 6s, 12s, 24s
        continue;
      }
      
      // For other errors or max retries exceeded, log and return empty array
      if (errWithResponse?.response?.status === 429) {
        debug(`[getPublicCreatorsList] Rate limited - max retries (${maxRetries}) exceeded, returning empty array`);
      } else {
        error(`[getPublicCreatorsList] Error fetching public creators list (attempt ${retryCount + 1}):`, err);
      }
      return [];
    }
  }
  
  return [];
}

/**
 * Fetches public featured users for unauthenticated users.
 * Uses the single public featured-creators endpoint (no profile_type). Same list used for both creator and advisor sections.
 */
export async function getPublicFeaturedUsersByProfileType(
  _profileType: FeaturedProfileType,
  limit: number = 10,
  options?: FetchRequestOptions
): Promise<User[]> {
  const maxRetries = 5;
  let retryCount = 0;
  let delay = 1500;
  const cappedLimit = Math.min(Math.max(limit, 1), 50);

  while (retryCount <= maxRetries) {
    try {
      if (options?.signal?.aborted) {
        return [];
      }
      const response = await axios.get(PUBLIC_CREATORS_LIST, {
        params: { limit: cappedLimit },
        signal: options?.signal,
      });
      const list = response.data?.creators ?? response.data;
      if (!Array.isArray(list)) return [];
      return list.map((item: Record<string, unknown>) => new User(item));
    } catch (err: unknown) {
      if (isRequestAborted(err)) {
        return [];
      }
      const errWithResponse = err as { response?: { status?: number } } | null;
      if (errWithResponse?.response?.status === 429 && retryCount < maxRetries) {
        retryCount++;
        debug(`[getPublicFeaturedUsersByProfileType] Rate limited (429), retry ${retryCount}/${maxRetries} after ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        delay *= 2;
        continue;
      }
      error('[getPublicFeaturedUsersByProfileType] Error fetching public featured list:', err);
      return [];
    }
  }
  return [];
}

/**
 * Fetches featured users by profile type for guest OPEN browse.
 * Uses GET /api/v2/public/users/featured (OPEN mode only).
 */
export async function getOpenBrowseFeaturedUsersByProfileType(
  profileType: FeaturedProfileType,
  limit: number = 10,
  options?: FetchRequestOptions
): Promise<User[]> {
  try {
    if (options?.signal?.aborted) {
      return [];
    }

    const cappedLimit = Math.min(Math.max(limit, 1), 50);
    const response = await axios.get(PUBLIC_OPEN_BROWSE_USERS_FEATURED, {
      params: { profile_type: profileType, limit: cappedLimit },
      signal: options?.signal,
    });

    const list = response.data;
    if (!Array.isArray(list)) return [];
    return list.map((item: Record<string, unknown>) => new User(item));
  } catch (err: unknown) {
    if (isRequestAborted(err)) {
      return [];
    }
    error(`[getOpenBrowseFeaturedUsersByProfileType] Error fetching featured ${profileType}s:`, err);
    return [];
  }
}

/**
 * Fetches all users from the /api/v1/users endpoint
 * Note: This endpoint requires authentication but has no admin-only restrictions
 * @returns Array of all users in the database
 */
type BasicUserIdentifier = {
  _id?: string;
  id?: string;
  email?: string;
};

export async function getAllUsers(): Promise<User[]> {
  try {
    const headers = getAuthHeaders();
    const response = await axios.get(ALL_USERS, { headers });
    const data = response.data;
    const totalUsers = Array.isArray(data) ? data.length : 0;

    const sampleUserIds = Array.isArray(data)
      ? (data.slice(0, 3) as BasicUserIdentifier[]).map(
          (user) => user._id || user.id || user.email || 'unknown'
        )
      : [];

    debug('[auth:getAllUsers] Retrieved users', {
      totalUsers,
      sampleUserIds,
    });
    return Array.isArray(data)
      ? data.map((item: Record<string, unknown>) => new User(item))
      : [];
  } catch (err) {
    error("Error fetching all users:", err);
    return [];
  }
}

export interface AdminUserPostsOptions {
  searchString?: string;
  onlyDrafts?: boolean;
  onlyPosts?: boolean;
  onlyItineraries?: boolean;
  onlyPrivate?: boolean;
  ignoreQuestions?: boolean;
  ignoreReplies?: boolean;
  limit?: number;
}

export async function fetchAdminUserProfile(userId: string): Promise<User> {
  if (!userId) {
    throw new Error('User ID is required to fetch admin user profile.');
  }

  try {
    const headers = getAuthHeaders();
    const response = await axios.get(FETCH_USER, {
      headers,
      params: { user_id: userId },
    });
    return new User(response.data);
  } catch (err) {
    error("Error fetching admin user profile:", err);
    throw err;
  }
}

export async function fetchAdminUserPosts(userId: string, options: AdminUserPostsOptions = {}): Promise<Post[]> {
  if (!userId) {
    throw new Error('User ID is required to fetch admin user posts.');
  }

  try {
    const headers = getAuthHeaders();
    const {
      searchString,
      onlyDrafts,
      onlyPosts,
      onlyItineraries,
      onlyPrivate,
      ignoreQuestions,
      ignoreReplies,
      limit = 200,
    } = options;

    const params: Record<string, string | number> = {
      user_id: userId,
      limit,
      photoFormat: "identifiers",
    };

    if (searchString) {
      params.searchString = searchString;
    }

    const booleanParams: Array<[string, boolean | undefined]> = [
      ['onlyDrafts', onlyDrafts],
      ['onlyPosts', onlyPosts],
      ['onlyItineraries', onlyItineraries],
      ['onlyPrivate', onlyPrivate],
      ['ignoreQuestions', ignoreQuestions],
      ['ignoreReplies', ignoreReplies],
    ];

    booleanParams.forEach(([key, value]) => {
      if (typeof value === 'boolean') {
        params[key] = value ? 'true' : 'false';
      }
    });

    const response = await axios.get(PROFILE_LIST, {
      headers,
      params,
    });
    return Array.isArray(response.data)
      ? response.data.map((item: Record<string, unknown>) => new Post(item))
      : [];
  } catch (err) {
    error("Error fetching admin user posts:", err);
    throw err;
  }
}

interface RawPostData {
  _id: string;
  user_id?: string;
  status?: string;
  post_type?: string;
  name?: string;
  is_hidden?: boolean;
  is_private?: boolean;
  is_draft?: boolean;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

export interface AdminPostSearchResponse {
  posts: Post[];
  rawPosts?: RawPostData[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface AdminUserSearchResponse {
  users: User[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface ChangePostOwnershipResponse {
  success: boolean;
  postId: string;
  oldOwnerId: string;
  newOwnerId: string;
  cascadeUpdates: {
    postsUpdated: number;
    remixMetricsUpdated: number;
    bookingsUpdated: number;
  };
}

export async function searchAdminPosts(params: {
  q: string;
  limit?: number;
  page?: number;
}): Promise<AdminPostSearchResponse> {
  const { q, limit = 20, page = 1 } = params;

  const trimmed = q.trim();
  if (!trimmed) {
    throw new Error('Search query is required');
  }

  if (trimmed.length < 2) {
    throw new Error('Search query must be at least 2 characters');
  }

  try {
    const headers = getAuthHeaders();
    const response = await axios.get(
      `${API_URL}/api/v2/admin/posts/search`,
      {
        headers,
        params: {
          searchString: trimmed,
          limit,
          page,
        },
      }
    );

    const data = response.data;
    
    // Store raw posts data before converting to Post objects (needed for user_id)
    const rawPosts = Array.isArray(data.posts) ? data.posts : [];
    
    // Debug: Log first post to see all available fields
    if (rawPosts.length > 0) {
      const samplePost = rawPosts[0];
      debug('[searchAdminPosts] Sample post from backend', {
        postId: samplePost._id,
        postName: samplePost.name,
        allKeys: Object.keys(samplePost),
        hasUser: !!samplePost.user,
        hasDisplayUser: !!samplePost.displayUser,
        hasUserId: !!samplePost.user_id,
        hasUser_id: !!samplePost.user_id,
        userId: samplePost.user_id || samplePost.userId || 'N/A',
      });
    }
    
    return {
      posts: rawPosts.map((item: Record<string, unknown>) => new Post(item)),
      rawPosts, // Include raw data so we can access user_id
      total: data.total ?? 0,
      page: data.page ?? page,
      limit: data.limit ?? limit,
      hasMore: data.hasMore ?? false,
    };
  } catch (err) {
    error('[searchAdminPosts] Error searching posts:', err);
    throw err;
  }
}

export type ProfileType = 'regular' | 'creator' | 'advisor' | 'other';

export type UserStatusFilter = 'hidden' | 'banned' | 'deleted' | 'featured' | 'internal';

export interface AdminUserSearchParams {
  q?: string;
  limit?: number;
  page?: number;
  profile_type?: ProfileType;
  // Status filters - filter by user status flags
  is_hidden?: boolean;
  is_banned?: boolean;
  is_deleted?: boolean;
  is_featured?: boolean;
  is_internal?: boolean;
  // Pricing filters
  is_gt_plus?: boolean;
  has_custom_margin?: boolean;
  advisor_pro_status?: 'none' | 'pending_payment' | 'active' | 'past_due' | 'canceled';
  advisor_verification_status?: 'unsubmitted' | 'pending' | 'verified' | 'rejected';
  // Get all users (when true, searchString is optional)
  get_all?: boolean;
}

export async function searchAdminUsers(params: AdminUserSearchParams): Promise<AdminUserSearchResponse> {
  const {
    q,
    limit = 20,
    page = 1,
    profile_type,
    is_hidden,
    is_banned,
    is_deleted,
    is_featured,
    is_internal,
    is_gt_plus,
    has_custom_margin,
    advisor_pro_status,
    advisor_verification_status,
    get_all
  } = params;

  const trimmed = q?.trim() ?? '';
  
  // Check if any filter is provided
  const hasStatusFilter = is_hidden !== undefined || is_banned !== undefined || is_deleted !== undefined || is_featured !== undefined || is_internal !== undefined;
  const hasPricingFilter = is_gt_plus !== undefined || has_custom_margin !== undefined;
  const hasAdvisorProFilter =
    advisor_pro_status !== undefined || advisor_verification_status !== undefined;
  const hasAnyFilter = !!profile_type || hasStatusFilter || hasPricingFilter || hasAdvisorProFilter;
  
  // Backend: searchString is required only when NO filters and NO get_all are provided
  // When filters (profile_type, is_hidden, is_banned, is_deleted) or get_all=true are provided, searchString is optional
  if (!trimmed && !hasAnyFilter && !get_all) {
    throw new Error('Search query, at least one filter (profile_type, is_hidden, is_banned, is_deleted), or get_all=true is required');
  }

  // If search query is provided, validate length (only when no filters/get_all, or as additional search)
  if (trimmed && trimmed.length < 2 && !get_all) {
    throw new Error('Search query must be at least 2 characters (or use get_all=true to get all users)');
  }

  try {
    const headers = getAuthHeaders();
    
    // Build request params
    const requestParams: Record<string, string | number | boolean> = {
      limit,
      page,
    };
    
    // Backend: searchString is optional when filters or get_all are provided
    // Only include searchString if actually provided
    if (trimmed) {
      requestParams.searchString = trimmed;
    }
    
    // Add get_all parameter if provided
    if (get_all) {
      requestParams.get_all = get_all;
    }
    
    // Add profile_type filter if provided
    if (profile_type) {
      requestParams.profile_type = profile_type;
    }
    
    // Add status filters if provided
    if (is_hidden !== undefined) {
      requestParams.is_hidden = is_hidden;
    }
    if (is_banned !== undefined) {
      requestParams.is_banned = is_banned;
    }
    if (is_deleted !== undefined) {
      requestParams.is_deleted = is_deleted;
    }
    if (is_featured !== undefined) {
      requestParams.is_featured = is_featured;
    }
    if (is_internal !== undefined) {
      requestParams.is_internal = is_internal;
    }
    if (is_gt_plus !== undefined) {
      requestParams.is_gt_plus = is_gt_plus;
    }
    if (has_custom_margin !== undefined) {
      requestParams.has_custom_margin = has_custom_margin;
    }
    if (advisor_pro_status !== undefined) {
      requestParams.advisor_pro_status = advisor_pro_status;
    }
    if (advisor_verification_status !== undefined) {
      requestParams.advisor_verification_status = advisor_verification_status;
    }
    
    const response = await axios.get(
      `${API_URL}/api/v2/admin/users/search`,
      {
        headers,
        params: requestParams,
      }
    );

    const data = response.data;
    return {
      users: Array.isArray(data.users) ? data.users.map((item: unknown) => new User(item as Record<string, unknown>)) : [],
      total: data.total ?? 0,
      page: data.page ?? page,
      limit: data.limit ?? limit,
      hasMore: data.hasMore ?? false,
    };
  } catch (err) {
    error('[searchAdminUsers] Error searching users:', err);
    throw err;
  }
}

export async function changePostOwnership(
  postId: string,
  newOwnerId: string
): Promise<ChangePostOwnershipResponse> {
  if (!postId || !newOwnerId) {
    throw new Error('Post ID and new owner ID are required');
  }

  try {
    const headers = getAuthHeaders();
    const response = await axios.patch(
      `${API_URL}/api/v2/admin/posts/${postId}/owner`,
      { newOwnerId },
      { headers }
    );

    const data = response.data;
    
    debug('[changePostOwnership] Backend response', {
      success: data.success,
      postId: data.postId,
      oldOwnerId: data.oldOwnerId,
      newOwnerId: data.newOwnerId,
      cascadeUpdates: data.cascadeUpdates,
    });
    
    if (!data.success) {
      throw new Error('Backend reported failure');
    }
    
    return {
      success: data.success,
      postId: data.postId,
      oldOwnerId: data.oldOwnerId,
      newOwnerId: data.newOwnerId,
      cascadeUpdates: data.cascadeUpdates ?? {
        postsUpdated: 0,
        remixMetricsUpdated: 0,
        bookingsUpdated: 0,
      },
    };
  } catch (err) {
    error('[changePostOwnership] Error changing post ownership:', err);
    throw err;
  }
}

// ============================================================================
// Admin User Type Management
// ============================================================================

export interface AdminUserUpdateRequest {
  profile_type?: ProfileType;
  is_approved?: boolean;
  is_featured?: boolean;
  is_hidden?: boolean;
  is_banned?: boolean;
  is_deleted?: boolean;
  book_url?: string | null;
  is_gt_plus?: boolean;
  is_internal?: boolean;
  hotel_margin?: number | null;
}

export interface ChangeDetail {
  old: string | boolean | null;
  new: string | boolean | null;
  auto?: boolean;
}

export interface AdminUserUpdateResponse {
  success: boolean;
  changed: boolean;
  userId: string;
  oldValues: {
    profile_type: string;
    is_approved: boolean;
    is_featured: boolean;
    is_hidden: boolean;
    is_banned: boolean;
    is_deleted: boolean;
    book_url: string | null;
    is_gt_plus?: boolean;
    is_internal?: boolean;
    hotel_margin?: number | null;
  };
  newValues: {
    profile_type: string;
    is_approved: boolean;
    is_featured: boolean;
    is_hidden: boolean;
    is_banned: boolean;
    is_deleted: boolean;
    book_url: string | null;
    is_gt_plus?: boolean;
    is_internal?: boolean;
    hotel_margin?: number | null;
  };
  changes: {
    profile_type?: ChangeDetail;
    is_approved?: ChangeDetail;
    is_featured?: ChangeDetail;
    is_hidden?: ChangeDetail;
    is_banned?: ChangeDetail;
    is_deleted?: ChangeDetail;
    book_url?: ChangeDetail;
    is_gt_plus?: ChangeDetail;
    is_internal?: ChangeDetail;
    hotel_margin?: ChangeDetail;
  };
}

export interface AdminUserTypeCounts {
  regular: number;
  creator: number;
  advisor: number;
  other: number;
  total: number;
  // Status counts (backend may or may not return these)
  hidden?: number;
  banned?: number;
  deleted?: number;
  featured?: number;
  internal?: number;
  // Pricing counts
  gt_plus?: number;
  custom_margin?: number;
  // Advisor Pro counts
  advisor_pro_active?: number;
  advisor_pro_past_due?: number;
  advisor_pro_canceled?: number;
  advisor_pro_pending_verification?: number;
  advisor_pro_verified?: number;
}

/**
 * Update user type and status (admin only)
 * @param userId - The ID of the user to update
 * @param updates - Fields to update (profile_type, is_approved, is_featured, is_hidden, is_banned)
 * @returns Update result with old/new values and changes
 */
export async function updateAdminUser(
  userId: string,
  updates: AdminUserUpdateRequest
): Promise<AdminUserUpdateResponse> {
  if (!userId) {
    throw new Error('User ID is required');
  }

  // Validate at least one field is provided
  if (
    updates.profile_type === undefined &&
    updates.is_approved === undefined &&
    updates.is_featured === undefined &&
    updates.is_hidden === undefined &&
    updates.is_banned === undefined &&
    updates.is_deleted === undefined &&
    updates.book_url === undefined &&
    updates.is_gt_plus === undefined &&
    updates.is_internal === undefined &&
    updates.hotel_margin === undefined
  ) {
    throw new Error('At least one field must be provided');
  }

  // Validate profile_type if provided
  if (updates.profile_type !== undefined) {
    const validTypes: ProfileType[] = ['regular', 'creator', 'advisor'];
    if (!validTypes.includes(updates.profile_type)) {
      throw new Error('profile_type must be one of: regular, creator, advisor');
    }
  }

  try {
    const headers = getAuthHeaders();
    const response = await axios.patch(
      `${API_URL}/api/v2/admin/users/${userId}`,
      updates,
      { headers }
    );

    const data = response.data;

    debug('[updateAdminUser] Backend response', {
      success: data.success,
      changed: data.changed,
      userId: data.userId,
      changes: data.changes,
    });

    return {
      success: data.success ?? true,
      changed: data.changed ?? false,
      userId: data.userId ?? userId,
      oldValues: data.oldValues ?? {
        profile_type: 'regular',
        is_approved: true,
        is_featured: false,
        is_hidden: false,
        is_banned: false,
        is_deleted: false,
        book_url: null,
      },
      newValues: data.newValues ?? {
        profile_type: updates.profile_type ?? 'regular',
        is_approved: updates.is_approved ?? true,
        is_featured: updates.is_featured ?? false,
        is_hidden: updates.is_hidden ?? false,
        is_banned: updates.is_banned ?? false,
        is_deleted: updates.is_deleted ?? false,
        book_url: updates.book_url ?? null,
      },
      changes: data.changes ?? {},
    };
  } catch (err) {
    error('[updateAdminUser] Error updating user:', err);
    
    // Re-throw with more specific error messages based on status code
    if (axios.isAxiosError(err)) {
      const status = err.response?.status;
      const message = err.response?.data?.message || err.message;
      
      switch (status) {
        case 400:
          throw new Error(`Invalid request: ${message}`);
        case 403:
          throw new Error('Admin access required');
        case 404:
          throw new Error('User not found');
        case 429:
          throw new Error('Rate limit exceeded. Please try again later.');
        default:
          throw new Error(message || 'Failed to update user');
      }
    }
    
    throw err;
  }
}

/**
 * Get counts of users by profile type (admin only)
 * @returns Counts for each profile type and total
 */
export async function getAdminUserTypeCounts(): Promise<AdminUserTypeCounts> {
  try {
    const headers = getAuthHeaders();
    const response = await axios.get(
      `${API_URL}/api/v2/admin/users/counts`,
      { headers }
    );

    const data = response.data;
    
    // Backend may return data nested under 'counts' or directly
    const counts = data.counts || data;

    debug('[getAdminUserTypeCounts] Backend response', data);

    return {
      regular: counts.regular ?? 0,
      creator: counts.creator ?? 0,
      advisor: counts.advisor ?? 0,
      other: counts.other ?? 0,
      total: counts.total ?? 0,
      // Status counts - backend returns these if available
      hidden: counts.hidden,
      banned: counts.banned,
      deleted: counts.deleted,
      featured: counts.featured,
      internal: counts.internal,
      // Pricing counts
      gt_plus: counts.gt_plus,
      custom_margin: counts.custom_margin,
      // Advisor Pro counts
      advisor_pro_active: counts.advisor_pro_active,
      advisor_pro_past_due: counts.advisor_pro_past_due,
      advisor_pro_canceled: counts.advisor_pro_canceled,
      advisor_pro_pending_verification: counts.advisor_pro_pending_verification,
      advisor_pro_verified: counts.advisor_pro_verified,
    };
  } catch (err) {
    error('[getAdminUserTypeCounts] Error fetching user type counts:', err);
    throw err;
  }
}

/**
 * Get users filtered by profile type using public endpoint (no auth required)
 * Uses /api/v1/users/filtered which doesn't require a searchString
 * 
 * Note: For admin use cases, prefer searchAdminUsers() which now supports
 * profile_type filtering without searchString and works consistently with status filters.
 * 
 * @param profileType - The profile type to filter by
 * @param limit - Number of users to return (max 100, default 50)
 * @param page - Page number for pagination (1-indexed)
 * @returns List of users with the specified profile type
 */
export async function getUsersByType(
  profileType: ProfileType | 'regular',
  limit: number = 50,
  page: number = 1,
  browseMode?: PlatformBrowseMode
): Promise<AdminUserSearchResponse> {
  try {
    const { getFilteredUsers } = await import('@/api/search');
    const data = await getFilteredUsers({
      profileType,
      limit: Math.min(limit, 100),
      page,
      browseMode,
    });

    debug('[getUsersByType] Backend response', data);

    return {
      users: data.users,
      total: data.total ?? 0,
      page: data.page ?? page,
      limit: data.limit ?? limit,
      hasMore: data.hasMore ?? false,
    };
  } catch (err) {
    error('[getUsersByType] Error fetching users by type:', err);
    throw err;
  }
}

/**
 * Get users filtered by profile type (admin only)
 * Note: Backend requires searchString, so we delegate to searchAdminUsers which handles this
 * @deprecated Use getUsersByType instead for filter-only queries
 * @param profileType - The profile type to filter by
 * @param limit - Number of users to return
 * @param page - Page number for pagination
 * @returns List of users with the specified profile type
 */
export async function getAdminUsersByType(
  profileType: ProfileType,
  limit: number = 20,
  page: number = 1
): Promise<AdminUserSearchResponse> {
  // Delegate to searchAdminUsers which handles the searchString requirement
  return searchAdminUsers({
    profile_type: profileType,
    limit,
    page,
  });
}

export async function deleteAccount(): Promise<void> {
  try {
    const headers = getAuthHeaders();
    await axios.get(DELETE_USER, { headers });
    debug('[deleteAccount] Account deleted successfully');

    if (typeof window !== 'undefined') {
      localStorage.clear();
    }
  } catch (err) {
    error('[deleteAccount] Error deleting account:', err);
    throw err;
  }
}

export async function updateUserProfile(profileData: UserProfilePayload): Promise<User> {
  try {
    // Use POST /api/v2/user endpoint (same as mobile app) for consistency
    // This endpoint requires Authorization header (mandatory) and optionally x-id-token header
    const rawAccessToken = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
    const rawIdToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;

    if (!rawAccessToken) {
      throw new Error('Authentication token not found. Please log in again.');
    }

    // Backend expects raw JWT token, NOT prefixed with "Bearer "
    // Strip "Bearer " prefix if present (some Cognito SDKs add it)
    const accessToken = rawAccessToken.startsWith('Bearer ')
      ? rawAccessToken.substring(7)
      : rawAccessToken;

    // ID token is optional but recommended for v2 API
    const idToken = rawIdToken
      ? (rawIdToken.startsWith('Bearer ') ? rawIdToken.substring(7) : rawIdToken)
      : null;

    // Clean up the profile data - remove fields that shouldn't be sent
    const cleanedData: Partial<UserProfilePayload> = {};

    // Only include specific allowed fields that can be updated
    // This prevents issues with MongoDB-specific fields or unexpected data types
    const allowedFields = PROFILE_UPDATE_ALLOWED_FIELDS;

    // Copy only allowed fields from profileData
    // For updates, we typically only send the fields that are changing
    // IMPORTANT: Only include fields that have actual values (not undefined, null, or empty strings)
    // MongoDB rejects undefined values and will throw errors like "Updating the path 'profile_type' would create a conflict"
    allowedFields.forEach((field) => {
      const value = profileData?.[field];

      if (value === undefined) {
        return;
      }

      if (value === null) {
        if (isSocialHandleField(field)) {
          cleanedData[field] = null;
        }
        return;
      }

      if (isArrayField(field)) {
        if (isStringArray(value)) {
          cleanedData[field] = value;
        }
        return;
      }

      if (typeof value === 'object' && !Array.isArray(value)) {
        if (field === 'city') {
          cleanedData[field] = value as UserProfilePayload[typeof field];
        }
        return;
      }

      if (typeof value === 'string') {
        if (isSocialHandleField(field)) {
          if (value !== '') {
            cleanedData[field] = value;
          }
          return;
        }
        if (value !== '') {
          cleanedData[field] = value;
        }
      }
    });

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': accessToken // Raw JWT token (no "Bearer " prefix) - REQUIRED
    };

    // ID token is optional but recommended for v2 API
    if (idToken) {
      headers['x-id-token'] = idToken;
    }

    // Use /api/v2/user endpoint (same as mobile app and createUser) for consistency
    const response = await axios.post(`${CREATE_USER}`, cleanedData, {
      headers
    });

    debug('[updateUserProfile] Profile updated successfully:', response.data);
    return new User(response.data);
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const classified = classifyAuthFailure(err.response?.status, err.response?.data);
      if (classified) throw classified;
    }
    error("[updateUserProfile] Error updating user profile:", err);
    if (axios.isAxiosError(err)) {
      const { config, response } = err;

      if (config) {
        debug("[updateUserProfile] Error details:", {
          attemptedUrl: config.url,
          requestMethod: config.method,
          requestHeaders: Object.keys(config.headers || {}),
          responseStatus: response?.status,
          responseData: response?.data,
          responseHeaders: response?.headers,
        });
      }

      if (response) {
        if (response.status === 403) {
          error("[updateUserProfile] Authentication failed - Authorization header may be missing or invalid");
          throw new Error('Authentication failed. Please log in again.');
        }

        const message = (response.data as { message?: string } | undefined)?.message;
        if (message) {
          debug("[updateUserProfile] Error message:", message);
        }
        const responseError = (response.data as { error?: string } | undefined)?.error;
        if (responseError) {
          debug("[updateUserProfile] Error details:", responseError);
        }
      }

      if (config?.data) {
        try {
          const sentData = typeof config.data === 'string'
            ? JSON.parse(config.data)
            : config.data;
          debug("[updateUserProfile] Request data sent:", sentData);
        } catch {
          debug("[updateUserProfile] Request data (raw):", config.data);
        }
      }
    }
    throw err;
  }
}

export async function hidePost(postId: string): Promise<{ success: boolean; insertedId?: string }> {
  debug('[hidePost] Starting hide post process', { postId });

  try {
    // Get tokens from localStorage
    const rawAccessToken = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
    const rawIdToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;

    debug('[hidePost] Token check', {
      hasAccessToken: !!rawAccessToken,
      hasIdToken: !!rawIdToken
    });

    if (!rawAccessToken) {
      error("[hidePost] No access token found in localStorage");
      throw new Error('Authentication token not found. User must be logged in.');
    }

    // Clean tokens (remove "Bearer " prefix if present)
    const accessToken = rawAccessToken.startsWith('Bearer ')
      ? rawAccessToken.substring(7)
      : rawAccessToken;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': accessToken // Raw JWT token (no "Bearer " prefix) - REQUIRED
    };

    // ID token is optional but recommended
    const idToken = rawIdToken
      ? (rawIdToken.startsWith('Bearer ') ? rawIdToken.substring(7) : rawIdToken)
      : null;

    if (idToken) {
      headers['x-id-token'] = idToken;
    }

    debug('[hidePost] Making API request', {
      url: HIDE_POST,
      postId,
      hasIdToken: !!idToken
    });

    const response = await axios.post(
      HIDE_POST,
      { postId },
      { headers }
    );

    debug('[hidePost] Post hidden successfully', {
      postId,
      insertedId: response.data.insertedId,
      responseStatus: response.status
    });

    return {
      success: true,
      insertedId: response.data.insertedId
    };
  } catch (err) {
    error("[hidePost] Error hiding post", {
      postId,
      error: err,
    });

    if (axios.isAxiosError(err)) {
      const status = err.response?.status;
      const responseData = err.response?.data as { error?: string } | undefined;
      const errorMessage = responseData?.error || (status ? `HTTP ${status}` : 'Failed to hide post.');

      debug('[hidePost] API error response', {
        postId,
        status,
        errorMessage
      });

      if (status === 401 || status === 403) {
        debug('[hidePost] Authentication error - redirecting to logged-out view');
        // Clear tokens and redirect to logged-out view on auth errors
        // Onboarding is only reached after successful login when user hasn't completed onboarding
        if (typeof window !== 'undefined') {
          localStorage.clear();
          window.location.href = '/';
        }
      }

      throw new Error(errorMessage);
    }

    throw err;
  }
}

/**
 * Fetches the country count for a user
 * Uses the lightweight endpoint for profile header display
 * @param userId - User ID
 * @returns Country count (defaults to 0 on error or if user not found)
 */
export async function getUserCountryCount(
  userId: string,
  options?: FetchRequestOptions
): Promise<number> {
  try {
    if (!userId) {
      debug('[getUserCountryCount] No userId provided, returning 0');
      return 0;
    }

    const headers = getAuthHeaders();
    const response = await axios.get(USER_COUNTRIES_COUNT(userId), {
      headers,
      signal: options?.signal,
    });
    
    const count = response.data?.count ?? 0;
    debug('[getUserCountryCount] Successfully fetched country count', {
      userId,
      count
    });
    
    return count;
  } catch (err) {
    if (isRequestAborted(err)) {
      throw err;
    }

    // Handle 404 gracefully (user not found or no countries) - return 0
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      debug('[getUserCountryCount] User not found or no countries (404), returning 0', { userId });
      return 0;
    }

    error('[getUserCountryCount] Error fetching country count:', err);
    // Return 0 on error for graceful degradation
    return 0;
  }
}

/**
 * Type definitions for countries API responses
 */
export interface UserCountriesResponse {
  count: number;
  countries: string[];
  home_country: string | null;
  countries_from_posts: string[];
  stats: {
    total_posts_with_locations: number;
    total_locations_with_country: number;
    home_country_included: boolean;
  };
  posts_by_country?: Record<string, CountryPostData>;
}

export interface CountryPostData {
  post_count: number;
  location_count: number;
  post_ids: string[];
  sample_locations: Array<{
    location_id: string;
    location_name: string;
    city: string;
  }>;
  posts?: Array<{
    _id: string;
    name: string;
    post_type: 'Itinerary' | 'List' | 'Post' | 'Question';
    thumbnail: string | null;
    published_at: string;
    locations?: Array<{
      location_id: string;
      location_name: string;
      city: string;
      geoposition?: { type: string; coordinates: [number, number] };
      latitude?: number;
      longitude?: number;
    }>;
  }>;
}

/**
 * Fetches full countries data for a user
 * @param userId - User ID
 * @param options - Options for including posts and post details
 * @returns Countries data or null if not found
 */
export async function getUserCountries(
  userId: string,
  options?: { includePosts?: boolean; includePostDetails?: boolean }
): Promise<UserCountriesResponse | null> {
  try {
    if (!userId) {
      debug('[getUserCountries] No userId provided, returning null');
      return null;
    }

    const params = new URLSearchParams();
    if (options?.includePosts) params.set('include_posts', 'true');
    if (options?.includePostDetails) params.set('include_post_details', 'true');
    
    const url = `${USER_COUNTRIES(userId)}${params.toString() ? '?' + params.toString() : ''}`;
    const headers = getAuthHeaders();
    const response = await axios.get(url, { headers });
    
    debug('[getUserCountries] Successfully fetched countries data', {
      userId,
      count: response.data?.count,
      hasPostsByCountry: !!response.data?.posts_by_country
    });
    
    return response.data as UserCountriesResponse;
  } catch (err) {
    // Handle 404 gracefully (user not found or no countries)
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      debug('[getUserCountries] User not found or no countries (404), returning null', { userId });
      return null;
    }
    
    error('[getUserCountries] Error fetching countries data:', err);
    return null;
  }
}

/**
 * Profile totals, counted by the backend from the owner's public content only — so every
 * viewer, signed in or not, sees the same numbers.
 */
export interface ProfileStats {
  countries: number;
  cities: number;
  continents: number;
  /** Recommendations the owner made themselves (the profile's "Recs"), not Place posts. */
  recommendations: number;
  trips: number;
  lists: number;
}

/** GET /api/v1/user/stats/:userId. Throws on failure so the caller can fall back. */
export async function fetchUserProfileStats(userId: string): Promise<ProfileStats> {
  const response = await axios.get<ProfileStats>(USER_STATS(userId), { headers: getAuthHeaders() });
  return response.data;
}

/**
 * Fetches every place a user has put their name to — locations from their published
 * Lists, published Trips (Itineraries), and their own recommendations — for the
 * profile's Experiences tab. Returns a flat array of raw Location documents.
 */
export async function fetchUserExperiences(userId: string): Promise<Location[]> {
  try {
    if (!userId) {
      debug('[fetchUserExperiences] No userId provided, returning empty list');
      return [];
    }

    const headers = getAuthHeaders();
    const response = await axios.get(USER_EXPERIENCES(userId), { headers });

    const locations = Array.isArray(response.data) ? response.data : [];
    debug('[fetchUserExperiences] Successfully fetched user experiences', {
      userId,
      count: locations.length,
    });

    return locations as Location[];
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      debug('[fetchUserExperiences] User not found or no experiences (404), returning empty list', { userId });
      return [];
    }

    error('[fetchUserExperiences] Error fetching user experiences:', err);
    return [];
  }
}

// ============================================================================
// Admin Platform Config
// ============================================================================

export interface PlatformConfig {
  gt_plus_hotel_margin: number | null;
  default_hotel_margin: number | null;
  browse_mode?: PlatformBrowseMode;
  updated_at?: string;
}

export async function getAdminPlatformConfig(): Promise<PlatformConfig> {
  try {
    const headers = getAuthHeaders();
    const response = await axios.get(
      `${API_URL}/api/v2/admin/platform-config`,
      { headers }
    );
    return response.data.data as PlatformConfig;
  } catch (err) {
    error('[getAdminPlatformConfig] Error fetching platform config:', err);
    throw err;
  }
}

export async function updateAdminPlatformConfig(
  updates: Partial<{
    gt_plus_hotel_margin: number | null;
    default_hotel_margin: number | null;
    browse_mode: PlatformBrowseMode;
  }>
): Promise<PlatformConfig> {
  try {
    const headers = getAuthHeaders();
    const response = await axios.patch(
      `${API_URL}/api/v2/admin/platform-config`,
      updates,
      { headers }
    );
    return response.data.data as PlatformConfig;
  } catch (err) {
    error('[updateAdminPlatformConfig] Error updating platform config:', err);
    throw err;
  }
}

// ============================================================================
// Admin Search Rewrite Config (Bedrock NLP search)
// ============================================================================

export interface SearchRewriteConfigUsingDefaults {
  systemPrompt: boolean;
  allowedTags: boolean;
  allowedTravelWithTypes: boolean;
}

export interface SearchRewriteConfigMeta {
  usingDefaults: SearchRewriteConfigUsingDefaults;
  updated_at: string | null;
  /** Built-in default system prompt text, when exposed by the backend. Enables "append to default" in the UI. */
  defaultSystemPrompt?: string | null;
}

export interface SearchRewriteConfig {
  systemPromptTemplate: string | null;
  /** When set, appended to the default prompt (ignored when systemPromptTemplate is set). */
  systemPromptAddition: string | null;
  allowedTags: string[];
  allowedTravelWithTypes: string[];
}

export interface SearchRewriteConfigResponse {
  data: SearchRewriteConfig;
  meta: SearchRewriteConfigMeta;
}

export type SearchRewriteConfigUpdate = {
  restoreDefaults?: true;
  systemPromptTemplate?: string | null;
  /** Appended to default prompt; max 16384 chars; empty string stored as null. Ignored when systemPromptTemplate is set. */
  systemPromptAddition?: string | null;
  allowedTags?: string[] | null;
  allowedTravelWithTypes?: string[] | null;
};

export async function getAdminSearchRewriteConfig(): Promise<SearchRewriteConfigResponse> {
  try {
    const headers = getAuthHeaders();
    const response = await axios.get(
      `${API_URL}/api/v2/admin/search-rewrite-config`,
      { headers }
    );
    return {
      data: response.data.data as SearchRewriteConfig,
      meta: response.data.meta as SearchRewriteConfigMeta,
    };
  } catch (err) {
    error('[getAdminSearchRewriteConfig] Error fetching search rewrite config:', err);
    throw err;
  }
}

export async function updateAdminSearchRewriteConfig(
  updates: SearchRewriteConfigUpdate
): Promise<SearchRewriteConfigResponse> {
  try {
    const headers = getAuthHeaders();
    const response = await axios.patch(
      `${API_URL}/api/v2/admin/search-rewrite-config`,
      updates,
      { headers }
    );
    return {
      data: response.data.data as SearchRewriteConfig,
      meta: response.data.meta as SearchRewriteConfigMeta,
    };
  } catch (err) {
    error('[updateAdminSearchRewriteConfig] Error updating search rewrite config:', err);
    throw err;
  }
}

// ============================================================================
// Admin Itinerary Rewrite Config (Bedrock vibe rewrite for prediction engine)
// ============================================================================

export interface ItineraryRewriteConfigUsingDefaults {
  systemPrompt: boolean;
  allowedEatTypes: boolean;
  allowedDoTypes: boolean;
}

export interface ItineraryRewriteConfigMeta {
  usingDefaults: ItineraryRewriteConfigUsingDefaults;
  updated_at: string | null;
  defaultSystemPrompt?: string | null;
}

export interface ItineraryRewriteConfig {
  systemPromptTemplate: string | null;
  systemPromptAddition: string | null;
  allowedEatTypes: string[];
  allowedDoTypes: string[];
}

export interface ItineraryRewriteConfigResponse {
  data: ItineraryRewriteConfig;
  meta: ItineraryRewriteConfigMeta;
}

export type ItineraryRewriteConfigUpdate = {
  restoreDefaults?: true;
  systemPromptTemplate?: string | null;
  systemPromptAddition?: string | null;
  allowedEatTypes?: string[] | null;
  allowedDoTypes?: string[] | null;
};

export async function getAdminItineraryRewriteConfig(): Promise<ItineraryRewriteConfigResponse> {
  try {
    const headers = getAuthHeaders();
    const response = await axios.get(
      `${API_URL}/api/v2/admin/itinerary-rewrite-config`,
      { headers }
    );
    return {
      data: response.data.data as ItineraryRewriteConfig,
      meta: response.data.meta as ItineraryRewriteConfigMeta,
    };
  } catch (err) {
    error('[getAdminItineraryRewriteConfig] Error fetching itinerary rewrite config:', err);
    throw err;
  }
}

export async function updateAdminItineraryRewriteConfig(
  updates: ItineraryRewriteConfigUpdate
): Promise<ItineraryRewriteConfigResponse> {
  try {
    const headers = getAuthHeaders();
    const response = await axios.patch(
      `${API_URL}/api/v2/admin/itinerary-rewrite-config`,
      updates,
      { headers }
    );
    return {
      data: response.data.data as ItineraryRewriteConfig,
      meta: response.data.meta as ItineraryRewriteConfigMeta,
    };
  } catch (err) {
    error('[updateAdminItineraryRewriteConfig] Error updating itinerary rewrite config:', err);
    throw err;
  }
}
